import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Edit, Trash2, Eye, Upload, X } from 'lucide-react';
import { productsAPI } from '../../api/products';
import { Product } from '../../types';
import toast from 'react-hot-toast';

const ProductsAdminPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [variants, setVariants] = useState<{
    size: string;
    color: string;
    color_hex: string;
    stock_quantity: number;
    sku: string;
  }[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price: '',
    compare_at_price: '',
    category_id: '',
    material: '',
    care_instructions: '',
    is_active: true,
    is_featured: false,
    requires_measurements: false,
    measurement_categories: [] as string[],
    is_nigeria_made: false,
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
  try {
    setIsLoading(true);
    const res = await productsAPI.getAll({ limit: 100, page: 1 });
    if (res.success && res.data) {
      setProducts(res.data.products || []);
    }
  } catch {
    toast.error('Failed to fetch products');
  } finally {
    setIsLoading(false);
  }
};

  const handleEdit = (product: Product) => {
  setEditingProduct(product);
  setFormData({
    name: product.name,
    slug: product.slug,
    description: product.description || '',
    price: String(product.price),
    compare_at_price: String(product.compare_at_price || ''),
    category_id: product.category_id || '',
    material: product.material || '',
    care_instructions: product.care_instructions || '',
    is_active: product.is_active,
    is_featured: product.is_featured,
    requires_measurements: Boolean((product as any).requires_measurements),
    measurement_categories: (product as any).measurement_categories || [],
    is_nigeria_made: Boolean((product as any).is_nigeria_made),
  });
  setUploadedImages(
    product.images?.map((img) => img.image_url) || []
  );
  setVariants(
    product.variants?.map((v) => ({
      size: v.size || '',
      color: v.color || '',
      color_hex: v.color_hex || '#000000',
      stock_quantity: v.stock_quantity || 0,
      sku: v.sku || '',
    })) || []
  );
  setShowForm(true);
};

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await productsAPI.delete(id);
      toast.success('Product deleted');
      fetchProducts();
    } catch {
      toast.error('Failed to delete product');
    }
  };

const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const files = e.target.files;
  if (!files || files.length === 0) return;
  try {
    setIsUploading(true);
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('file', file));
    const token = localStorage.getItem('lanre_savage_token');
    const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8080'}/api/upload/image`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
    const data = await res.json();
    if (data.success) {
     const urls = data.data.urls.map((url: string) => `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8080'}${url}`);
      setUploadedImages((prev) => [...prev, ...urls]);
      toast.success('Images uploaded');
    } else {
      toast.error('Upload failed');
    }
  } catch {
    toast.error('Failed to upload images');
  } finally {
    setIsUploading(false);
  }
};

  const addVariant = () => {
    setVariants((prev) => [
      ...prev,
      { size: '', color: '', color_hex: '#000000', stock_quantity: 0, sku: '' },
    ]);
  };

  const removeVariant = (index: number) => {
    setVariants((prev) => prev.filter((_, i) => i !== index));
  };

  const updateVariant = (index: number, field: string, value: string | number) => {
    setVariants((prev) =>
      prev.map((v, i) => (i === index ? { ...v, [field]: value } : v))
    );
  };

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (isSubmitting) return;
  try {
    setIsSubmitting(true);
    const data = {
      ...formData,
      price: parseFloat(formData.price),
      compare_at_price: formData.compare_at_price
        ? parseFloat(formData.compare_at_price)
        : undefined,
      category_id: formData.category_id || undefined,
      images: uploadedImages.map((url, index) => ({
        image_url: url,
        is_primary: index === 0,
        sort_order: index,
      })),
      variants: variants,
    };

    if (editingProduct) {
      await productsAPI.update(editingProduct.id, data);
      toast.success('Product updated');
    } else {
      await productsAPI.create(data);
      toast.success('Product created');
    }
    setShowForm(false);
    setEditingProduct(null);
    setUploadedImages([]);
    setVariants([]);
    fetchProducts();
  } catch (error: any) {
    toast.error(error.response?.data?.message || 'Failed to save product');
  } finally {
    setIsSubmitting(false);
  }
};

  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );
  const fetchCategories = async () => {
  try {
    const res = await productsAPI.getCategories();
    if (res.success && res.data) setCategories(res.data);
  } catch {}
};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs tracking-widest uppercase text-nude mb-1">
            Management
          </p>
          <h1 className="font-serif text-3xl text-primary">Products</h1>
        </div>
        <button
          onClick={() => {
            setEditingProduct(null);
            setUploadedImages([]);
            setVariants([]);
            setFormData({
              name: '', slug: '', description: '', price: '',
              compare_at_price: '', category_id: '', material: '',
              care_instructions: '', is_active: true, is_featured: false,
              requires_measurements: false, measurement_categories: [],
              is_nigeria_made: false,
            });
            setShowForm(true);
          }}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={14} />
          <span>Add Product</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nude" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="SEARCH PRODUCTS"
          className="w-full bg-white border border-light pl-9 pr-4 py-3 text-xs tracking-widest outline-none focus:border-primary transition-colors"
        />
      </div>

      {/* Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-light p-6"
        >
          <h2 className="font-serif text-xl text-primary mb-6">
            {editingProduct ? 'Edit Product' : 'New Product'}
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Name
              </label>
              <input
                value={formData.name}
                onChange={(e) => setFormData({
                  ...formData,
                  name: e.target.value,
                  slug: generateSlug(e.target.value),
                })}
                className="input-luxury"
                required
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Slug
              </label>
              <input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="input-luxury"
                required
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Price
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="input-luxury"
                required
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Compare At Price
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.compare_at_price}
                onChange={(e) => setFormData({
                  ...formData,
                  compare_at_price: e.target.value,
                })}
                className="input-luxury"
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Material
              </label>
              <input
                value={formData.material}
                onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                className="input-luxury"
              />
            </div>
            <div>
  <label className="text-xs tracking-widest uppercase text-nude block mb-2">
    Category
  </label>
  <select
    value={formData.category_id}
    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
    className="w-full border-b border-nude bg-transparent py-3 text-xs outline-none focus:border-primary transition-colors"
  >
    <option value="">No Category</option>
    {categories.map((cat) => (
      <option key={cat.id} value={cat.id}>
        {cat.name}
      </option>
    ))}
  </select>
</div>
            <div className="flex items-center space-x-6 pt-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({
                    ...formData,
                    is_active: e.target.checked,
                  })}
                  className="accent-primary"
                />
                <span className="text-xs tracking-widest uppercase">Active</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({
                    ...formData,
                    is_featured: e.target.checked,
                  })}
                  className="accent-primary"
                />
                <span className="text-xs tracking-widest uppercase">Featured</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.requires_measurements}
                  onChange={(e) => setFormData({
                    ...formData,
                    requires_measurements: e.target.checked,
                  })}
                  className="accent-primary"
                />
                <span className="text-xs tracking-widest uppercase">Made to Measure</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_nigeria_made}
                  onChange={(e) => setFormData({
                    ...formData,
                    is_nigeria_made: e.target.checked,
                  })}
                  className="accent-primary"
                />
                <span className="text-xs tracking-widest uppercase">Made in Nigeria</span>
              </label>
            </div>
            {formData.is_nigeria_made && (
              <p className="col-span-2 text-xs text-amber-600 leading-relaxed -mt-2">
                This item won't use standard USPS/Pirate Ship shipping. At checkout, customers choose Pick Up In Store, Local Nigeria Delivery, or International (weight-based) — the last two are quoted separately via WhatsApp rather than charged at checkout.
              </p>
            )}

            {formData.requires_measurements && (
              <div className="col-span-2 bg-light p-4">
                <p className="text-xs tracking-widest uppercase text-nude mb-3">
                  Which measurements does this piece need?
                </p>
                <div className="flex flex-wrap gap-4">
                  {[
                    { key: 'cap', label: 'Cap' },
                    { key: 'agbada', label: 'Agbada' },
                    { key: 'top', label: 'Top' },
                    { key: 'pant', label: 'Pant' },
                  ].map((cat) => (
                    <label key={cat.key} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.measurement_categories.includes(cat.key)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...formData.measurement_categories, cat.key]
                            : formData.measurement_categories.filter((c) => c !== cat.key);
                          setFormData({ ...formData, measurement_categories: next });
                        }}
                        className="accent-primary"
                      />
                      <span className="text-xs">{cat.label}</span>
                    </label>
                  ))}
                </div>
                {formData.measurement_categories.length === 0 && (
                  <p className="text-xs text-amber-600 mt-3">
                    Select at least one — otherwise the customer will see an empty form.
                  </p>
                )}
              </div>
            )}
            <div className="col-span-2">
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({
                  ...formData,
                  description: e.target.value,
                })}
                rows={4}
                className="w-full border border-light p-3 text-xs outline-none focus:border-primary transition-colors resize-none"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Care Instructions
              </label>
              <textarea
                value={formData.care_instructions}
                onChange={(e) => setFormData({
                  ...formData,
                  care_instructions: e.target.value,
                })}
                rows={2}
                className="w-full border border-light p-3 text-xs outline-none focus:border-primary transition-colors resize-none"
              />
            </div>

            {/* Image Upload */}
            <div className="col-span-2">
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Product Images
              </label>
              <div className="border-2 border-dashed border-light p-6 text-center">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer flex flex-col items-center space-y-2"
                >
                  <Upload size={24} className="text-nude" />
                  <span className="text-xs tracking-widest uppercase text-nude">
                    {isUploading ? 'Uploading...' : 'Click to upload images'}
                  </span>
                </label>
              </div>
              {uploadedImages.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-4">
                  {uploadedImages.map((url, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                      src={url.startsWith('http') ? url : `http://127.0.0.1:8080${url}`}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setUploadedImages((prev) =>
                          prev.filter((_, i) => i !== index)
                        )}
                        className="absolute top-1 right-1 bg-white rounded-full p-0.5"
                      >
                        <X size={10} className="text-primary" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Variants */}
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs tracking-widest uppercase text-nude">
                  Variants
                </label>
                <button
                  type="button"
                  onClick={addVariant}
                  className="text-xs tracking-widest uppercase text-primary hover:text-nude transition-colors flex items-center space-x-1"
                >
                  <Plus size={12} />
                  <span>Add Variant</span>
                </button>
              </div>
              {variants.length === 0 ? (
                <p className="text-xs text-nude tracking-wide">
                  No variants added. Click "Add Variant" to add sizes and colors.
                </p>
              ) : (
                <div className="space-y-3">
                  {variants.map((variant, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-6 gap-2 items-center border border-light p-3"
                    >
                      <input
                        placeholder="SIZE"
                        value={variant.size}
                        onChange={(e) => updateVariant(index, 'size', e.target.value)}
                        className="input-luxury text-xs col-span-1"
                      />
                      <input
                        placeholder="COLOR"
                        value={variant.color}
                        onChange={(e) => updateVariant(index, 'color', e.target.value)}
                        className="input-luxury text-xs col-span-1"
                      />
                      <div className="flex items-center space-x-2 col-span-1">
                        <input
                          type="color"
                          value={variant.color_hex}
                          onChange={(e) => updateVariant(index, 'color_hex', e.target.value)}
                          className="w-8 h-8 border border-light cursor-pointer"
                        />
                      </div>
                      <input
                        type="number"
                        placeholder="STOCK"
                        value={variant.stock_quantity}
                        onChange={(e) => updateVariant(index, 'stock_quantity', parseInt(e.target.value) || 0)}
                        className="input-luxury text-xs col-span-1"
                      />
                      <input
                        placeholder="SKU"
                        value={variant.sku}
                        onChange={(e) => updateVariant(index, 'sku', e.target.value)}
                        className="input-luxury text-xs col-span-1"
                      />
                      <button
                        type="button"
                        onClick={() => removeVariant(index)}
                        className="text-nude hover:text-red-500 transition-colors justify-self-end"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="col-span-2 flex space-x-3">
              <button type="submit" className="btn-primary" disabled={isSubmitting}>
  {isSubmitting ? 'Saving...' : editingProduct ? 'Update Product' : 'Create Product'}
</button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Table */}
      <div className="bg-white border border-light overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-light">
              {['Product', 'Price', 'Category', 'Status', 'Featured', 'Actions'].map((h) => (
                <th
                  key={h}
                  className="px-6 py-3 text-left text-xs tracking-widest uppercase text-nude"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center">
                  <div className="skeleton h-6 w-48 mx-auto rounded" />
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-xs text-nude tracking-widest uppercase">
                  No products found
                </td>
              </tr>
            ) : (
              filtered.map((product) => (
                <tr
                  key={product.id}
                  className="border-b border-light hover:bg-light/50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-12 bg-light flex-shrink-0 overflow-hidden">
                        {product.images?.[0] && (
                          <img
                            src={product.images[0].image_url}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div>
                        <p className="text-xs font-medium tracking-wide flex items-center gap-2">
                          {product.name}
                          {(product as any).requires_measurements && (
                            <span
                              className="text-[10px] tracking-widest uppercase text-amber-600 border border-amber-600 px-1.5 py-0.5"
                              title={((product as any).measurement_categories || []).join(', ') || 'No categories set'}
                            >
                              MTM
                            </span>
                          )}
                          {(product as any).is_nigeria_made && (
                            <span
                              className="text-[10px] tracking-widest uppercase text-green-700 border border-green-700 px-1.5 py-0.5"
                              title="Ships from Nigeria — pickup, local, or WhatsApp-quoted international"
                            >
                              NG
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-nude">{product.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-xs">
                    ${Number(product.price).toFixed(2)}
                    {product.compare_at_price && (
                      <span className="text-nude line-through ml-2">
                        ${Number(product.compare_at_price).toFixed(2)}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-xs text-nude">
                    {product.category_name || '—'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs tracking-widest uppercase px-2 py-1 ${
                      product.is_active
                        ? 'bg-green-50 text-green-600'
                        : 'bg-red-50 text-red-500'
                    }`}>
                      {product.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs ${
                      product.is_featured ? 'text-primary' : 'text-nude'
                    }`}>
                      {product.is_featured ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <a
                        href={`/products/${product.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-nude hover:text-primary transition-colors"
                      >
                        <Eye size={14} />
                      </a>
                      <button
                        onClick={() => handleEdit(product)}
                        className="text-nude hover:text-primary transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="text-nude hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductsAdminPage;